import {
  aosPictureApi,
  basiasBasolSisNearbySiteOtherApi,
  buildingApi,
  componentApi,
  otherItemApi,
  outdoorSpaceApi,
  polygonApi,
  siteApi,
  spreadingApi,
  zoneApi,
} from "@/api/cartographyClient";
import Marker from "@/models/cartography/Marker";
import Polygon from "@/models/cartography/Polygon";
import AosPicture from "@/models/cartography/AosPicture";
import { insertMarkers, insertAosItems, flushCartographyData } from "@/models/cartography/utils";

import _ from "lodash";
import { defineStore } from "pinia";
import { useRepo } from "pinia-orm";
import { useDocumentStore } from "./document";
import { filtersToParams } from "@/utils/restMetadata";
import { CHOICES } from "@/constants/cartography.js";
import { normalizePictureForm } from "@/utils/imgUtils";
import Building from "@/models/cartography/Building.js";
import Zone from "@/models/cartography/Zone.js";
import Component from "@/models/cartography/Component.js";
import AosOutdoorSpace from "@/models/cartography/AosOutdoorSpace.js";
import BasiasBasolSisNearbySiteOther from "@/models/cartography/BasiasBasolSisNearbySiteOther.js";
import OtherItem from "@/models/cartography/OtherItem.js";
import Spreading from "@/models/cartography/Spreading.js";

const MarkerRepo = useRepo(Marker);
const PolygonRepo = useRepo(Polygon);
const AosPictureRepo = useRepo(AosPicture);

const SITE = "SITE";
const BUILDING = "BUILDING";
const ZONE = "ZONE";
const COMPONENT = "COMPONENT";
const OTHER_ITEM = "OTHER_ITEM";
const OUTDOOR_SPACE = "OUTDOOR_SPACE";
const SPREADING = "SPREADING";
const BASIAS_BASOL_SIS_NEARBY_SITE_OTHER = "BASIAS_BASOL_SIS_NEARBY_SITE_OTHER";

const PAGE_SIZE = 100;

const typeToApiMapping = {
  [BUILDING]: buildingApi,
  [ZONE]: zoneApi,
  [SITE]: siteApi,
  [OTHER_ITEM]: otherItemApi,
  [COMPONENT]: componentApi,
  [OUTDOOR_SPACE]: outdoorSpaceApi,
  [SPREADING]: spreadingApi,
  [BASIAS_BASOL_SIS_NEARBY_SITE_OTHER]: basiasBasolSisNearbySiteOtherApi,
};

const typeToRequestKey = {
  [BUILDING]: "buildingRequest",
  [ZONE]: "zoneRequest",
  [SITE]: "siteRequest",
  [OTHER_ITEM]: "otherItemRequest",
  [COMPONENT]: "componentRequest",
  [OUTDOOR_SPACE]: "aosOutdoorSpaceRequest",
  [SPREADING]: "spreadingRequest",
  [BASIAS_BASOL_SIS_NEARBY_SITE_OTHER]: "basiasBasolSisNearbySiteOtherRequest",
};

const typeToPatchedRequestKey = {
  [BUILDING]: "patchedBuildingRequest",
  [ZONE]: "patchedZoneRequest",
  [SITE]: "patchedSiteRequest",
  [OTHER_ITEM]: "patchedOtherItemRequest",
  [COMPONENT]: "patchedComponentRequest",
  [OUTDOOR_SPACE]: "patchedAosOutdoorSpaceRequest",
  [SPREADING]: "patchedSpreadingRequest",
  [BASIAS_BASOL_SIS_NEARBY_SITE_OTHER]: "patchedBasiasBasolSisNearbySiteOtherRequest",
};

const typeToModelMapping = {
  [BUILDING]: Building,
  [ZONE]: Zone,
  [COMPONENT]: Component,
  [OUTDOOR_SPACE]: AosOutdoorSpace,
  [SPREADING]: Spreading,
  [OTHER_ITEM]: OtherItem,
  [BASIAS_BASOL_SIS_NEARBY_SITE_OTHER]: BasiasBasolSisNearbySiteOther,
};

const LOCATION_TYPOLOGIES = [BUILDING, OUTDOOR_SPACE, ZONE];

const excludeFields = ["createdAt", "updatedAt", "archiveDate"];

const fetchAllPages = async (api, params, aosType) => {
  const allResults = [];
  let page = 1;
  let response;
  do {
    response = await api.list({ ...params, page, pageSize: PAGE_SIZE });
    allResults.push(...(response.results || []).map((item) => ({ ...item, aosType })));
    page += 1;
  } while (response.next);
  return allResults;
};

export const useCartographyStore = defineStore("cartography", {
  state: () => ({
    siteUuid: null,
    isLoading: false,
    isUpdating: false,
    markersCount: 0,
    nextAvailableReference: null,
    locationOptions: [],
  }),

  getters: {
    getCurrentSiteUuid: (state) => {
      return state.siteUuid;
    },
    centralMarker:
      (state) =>
      (value = "") => {
        let marker = MarkerRepo.with("markable")
          .with("markers", (query) => query.with("markable"))
          .find(state.siteUuid);
        if (marker) {
          marker.markers = marker.markers.filter((nestedMarker) => {
            return nestedMarker.markable?.label?.includes(value);
          });
        }
        return marker;
      },
    isReferenceTaken: (state) => (reference, uuid) => {
      let marker = MarkerRepo.with("markable")
        .with("markers", (query) => query.with("markable"))
        .find(state.siteUuid);
      if (marker) {
        return marker.markers.some((nestedMarker) => {
          return (
            nestedMarker.markable?.reference == reference && nestedMarker.markable?.uuid != uuid
          );
        });
      }
      return false;
    },
    getAvailableReference: (state) => {
      if (state.nextAvailableReference) {
        return state.nextAvailableReference;
      } else {
        return 1;
      }
    },
    buildingsLocations: (state) => ({
      markers: state.locationOptions.filter((option) =>
        [BUILDING, OUTDOOR_SPACE].includes(option.markable?.typology),
      ),
    }),
    zonesLocations: (state) => ({
      markers: state.locationOptions.filter((option) => option.markable?.typology === ZONE),
    }),
    getAllElements: () => {
      const remarkableElements = [];
      for (const model of Object.values(typeToModelMapping)) {
        remarkableElements.push(...useRepo(model).all());
      }
      return remarkableElements;
    },
  },

  actions: {
    resetCartographyStore() {
      this.siteUuid = null;
      this.isLoading = false;
      this.isUpdating = false;
      this.markersCount = 0;
      this.nextAvailableReference = null;
      this.locationOptions = [];
      MarkerRepo.flush();
      PolygonRepo.flush();
      AosPictureRepo.flush();
    },
    async getCurrentSite(projectUuid) {
      if (!projectUuid) {
        this.isLoading = false;
        console.error("No project UUID provided");
        return null;
      }
      const { results: sites } = await siteApi.list({
        project: projectUuid,
        pageSize: PAGE_SIZE,
      });
      if (!sites.length) {
        this.siteUuid = null;
        this.isLoading = false;
        console.log("No site found");
        return null;
      }
      // INFO - B.L - Project can be link to only one site
      let site = sites[0];
      this.siteUuid = site.uuid;
      return site;
    },

    async createSite(form) {
      this.isUpdating = true;
      const response = await siteApi.create({ siteRequest: form });
      this.siteUuid = response.uuid;
      this.isUpdating = false;
      return response;
    },

    async fetchMarkers({ projectUuid, filters: extraFilters, pagination }) {
      flushCartographyData();
      MarkerRepo.flush();
      this.isLoading = true;
      if (!projectUuid) {
        this.siteUuid = null;
        this.isLoading = false;
        return null;
      }
      let site = await this.getCurrentSite(projectUuid);
      if (!site) {
        this.siteUuid = null;
        this.isLoading = false;
        return null;
      }
      insertMarkers(site);
      const filters = {
        site: site.uuid,
        isGeolocationNotNull: true,
        ...filtersToParams(extraFilters),
      };
      let types = [
        ZONE,
        COMPONENT,
        BUILDING,
        OUTDOOR_SPACE,
        SPREADING,
        BASIAS_BASOL_SIS_NEARBY_SITE_OTHER,
      ];
      if (filters.typology) {
        types = [filters.typology.toUpperCase()];
        delete filters.typology;
      }
      if (filters.type) {
        types = types.filter((type) => type !== ZONE && type !== OUTDOOR_SPACE);
      }
      if (types.length == 0) return { items: [], totalItems: 0 };

      const markers = await Promise.all(
        types.map((type) => fetchAllPages(typeToApiMapping[type], filters, type)),
      );

      const results = _.flatten(markers);
      const totalItems = results.length;
      if (pagination) {
        const { page = 1, pageSize = 10 } = pagination;
        const paginatedResults = results.slice((page - 1) * pageSize, page * pageSize);
        insertMarkers(paginatedResults);
        this.isLoading = false;
        this.markersCount = totalItems;
        return {
          items: paginatedResults,
          totalItems,
        };
      }

      insertMarkers(results);
      this.isLoading = false;
      this.markersCount = totalItems;
      return {
        items: results,
        totalItems,
      };
    },
    async fetchLocationOptions() {
      if (!this.siteUuid) {
        this.locationOptions = [];
        return this.locationOptions;
      }
      const responses = await Promise.all(
        LOCATION_TYPOLOGIES.map((typology) =>
          fetchAllPages(typeToApiMapping[typology], { site: this.siteUuid }, typology),
        ),
      );
      this.locationOptions = responses.flatMap((items, index) =>
        items.map((item) => ({
          markable: { ...item, typology: LOCATION_TYPOLOGIES[index] },
        })),
      );
      return this.locationOptions;
    },

    syncLocationOption({ typology, item }) {
      if (!LOCATION_TYPOLOGIES.includes(typology) || !item?.uuid) return;
      const option = { markable: { ...item, typology } };
      const index = this.locationOptions.findIndex(
        (existing) => existing.markable?.uuid === item.uuid,
      );
      if (index === -1) {
        this.locationOptions.push(option);
        return;
      }
      this.locationOptions.splice(index, 1, option);
    },

    removeLocationOption(uuid) {
      this.locationOptions = this.locationOptions.filter(
        (option) => option.markable?.uuid !== uuid,
      );
    },

    async createMarker({ aosType, form }) {
      this.isUpdating = true;
      const body = _.omit(form, ["uuid", "createdAt", "updatedAt", "aosType"]);
      const response = await typeToApiMapping[aosType].create({
        [typeToRequestKey[aosType]]: body,
      });
      insertMarkers(response);
      this.syncLocationOption({ typology: aosType, item: response });
      this.isUpdating = false;
      this.markersCount += 1;
      this.nextAvailableReference += 1;
      return MarkerRepo.with("markable").find(response.uuid);
    },

    async updateMarker(form) {
      this.isUpdating = true;
      const { typology } = form;

      const { uuid, ...body } = _.omit(form, excludeFields);
      const response = await typeToApiMapping[typology].partialUpdate({
        uuid,
        [typeToPatchedRequestKey[typology]]: body,
      });
      insertMarkers(response);
      this.syncLocationOption({ typology, item: response });
      this.isUpdating = false;
      return MarkerRepo.with("markable").find(response.uuid);
    },

    async partialUpdate({ data }) {
      this.isUpdating = true;
      const { typology } = data;
      const { uuid, ...body } = data;
      const response = await typeToApiMapping[typology].partialUpdate({
        uuid,
        [typeToPatchedRequestKey[typology]]: body,
      });
      insertAosItems(response);
      this.syncLocationOption({ typology, item: response });
      this.isUpdating = false;
    },

    async deleteMarker({ type, uuid }) {
      this.isUpdating = true;
      await typeToApiMapping[type].destroy({ uuid });
      MarkerRepo.destroy(uuid);
      this.removeLocationOption(uuid);
      this.isUpdating = false;
      this.markersCount -= 1;
    },

    async updateSiteCenter(form) {
      this.isUpdating = true;
      const { uuid, ...patchedSiteRequest } = form;
      const site = await siteApi.partialUpdate({ uuid, patchedSiteRequest });
      insertMarkers(site);
      this.isUpdating = false;
    },

    async fetchPolygons({ projectUuid }) {
      PolygonRepo.destroy();
      this.isLoading = true;
      // F.T 24/08/26 même invariant que fetchMarkers (TERRA-2778).
      if (!projectUuid) {
        this.siteUuid = null;
        this.isLoading = false;
        return [];
      }
      let site = await this.getCurrentSite(projectUuid);
      if (!site) {
        this.siteUuid = null;
        this.isLoading = false;
        return [];
      }
      const response = await polygonApi.list({
        site: site.uuid,
        pageSize: PAGE_SIZE,
      });
      if (!response.count) {
        this.isLoading = false;
        return [];
      }
      response.results.forEach((polygon) => {
        PolygonRepo.insert({
          uuid: polygon.uuid,
          coordinates: polygon.coordinates,
        });
      });
      this.isLoading = false;
    },

    async createPolygon(data) {
      this.isUpdating = true;
      const response = await polygonApi.create({ polygonRequest: data });
      PolygonRepo.insert({
        uuid: response.uuid,
        coordinates: response.coordinates,
      });
      this.isUpdating = false;
      return response.uuid;
    },

    async updatePolygon(data) {
      this.isUpdating = true;
      const { uuid, ...patchedPolygonRequest } = data;
      const response = await polygonApi.partialUpdate({
        uuid,
        patchedPolygonRequest,
      });
      PolygonRepo.insert({
        uuid: response.uuid,
        coordinates: response.coordinates,
      });
      this.isUpdating = false;
    },

    async deletePolygon({ uuid }) {
      this.isUpdating = true;
      await polygonApi.destroy({ uuid });
      PolygonRepo.destroy(uuid);
      this.isUpdating = false;
    },

    async fetchAosPictures(aosItemUuid) {
      AosPictureRepo.flush();
      const response = await aosPictureApi.list({
        aosItem: aosItemUuid,
        pageSize: PAGE_SIZE,
      });
      const documentStore = useDocumentStore();
      for (const picture of response.results) {
        if (!picture.pictureUuid) continue;
        try {
          const doc = await documentStore.getDocumentUrlAndName(picture.pictureUuid);
          AosPictureRepo.insert({
            uuid: picture.uuid,
            aosItem: picture.aosItem,
            isMain: picture.isMain,
            observation: picture.observation,
            pictureUuid: picture.pictureUuid,
            pictureUrl: doc.url,
            pictureName: doc.name,
            title: picture.title,
            year: picture.year,
          });
        } catch (error) {
          if (error?.response?.status === 404) {
            await aosPictureApi.destroy({ uuid: picture.uuid });
          }
        }
      }
    },

    async createOrUpdateAosPicture(form) {
      if (!form.pictureUuid) {
        console.error("pictureUuid is required but missing in form");
        return;
      }
      if (!form.aosItem) {
        console.error("aosItem is required but missing in form");
        return;
      }
      const normalizedForm = normalizePictureForm(form);
      let picture;
      if (normalizedForm.uuid) {
        const { uuid, ...patchedAosPictureRequest } = normalizedForm;
        picture = await aosPictureApi.partialUpdate({
          uuid,
          patchedAosPictureRequest,
        });
      } else {
        picture = await aosPictureApi.create({
          aosPictureRequest: normalizedForm,
        });
      }
      const { uuid, aosItem, isMain, observation, pictureUuid, title, year } = picture || {};
      const documentStore = useDocumentStore();
      const doc = await documentStore.getDocumentUrlAndName(pictureUuid);
      const data = {
        uuid,
        aosItem,
        isMain,
        observation,
        pictureUuid,
        pictureUrl: doc.url,
        pictureName: doc.name,
        title,
        year,
      };
      AosPictureRepo.save(data);
    },

    async deleteAosPicture(uuid) {
      await aosPictureApi.destroy({ uuid });
      AosPictureRepo.destroy(uuid);
    },

    async fetchItem({ type, uuid }) {
      const ModelRepo = useRepo(typeToModelMapping[type]);
      const item = await typeToApiMapping[type].retrieve({ uuid });
      return ModelRepo.save(item);
    },

    async fetchItems({ type, filters = {} }) {
      const ModelRepo = useRepo(typeToModelMapping[type]);
      const response = await typeToApiMapping[type].list({
        ...filtersToParams(filters),
        pageSize: filters.pageSize ?? PAGE_SIZE,
      });
      return ModelRepo.save(response.results);
    },

    async fetchSpecificInvestigations({ filters = {}, page = 1, pageSize = 10 }) {
      const response = await typeToApiMapping[COMPONENT].list({
        ...filtersToParams(filters),
        site: this.siteUuid,
        type: CHOICES.TYPES.COMPONENT.SPECIFIC_INVESTIGATION,
        page,
        pageSize,
      });
      useRepo(Component).save(response.results);
      return { items: response.results, totalItems: response.count };
    },

    async fetchAllItems({ type, filters, secondaryFilters }) {
      const ModelRepo = useRepo(typeToModelMapping[type]);
      ModelRepo.flush();
      const api = typeToApiMapping[type];
      ModelRepo.save(await fetchAllPages(api, filters, type));
      if (!secondaryFilters?.tagsType) {
        return;
      }
      ModelRepo.save(await fetchAllPages(api, secondaryFilters, type));
    },

    async getNextAvailableReference({ projectUuid, siteUuid }) {
      if (this.nextAvailableReference) {
        return this.nextAvailableReference;
      }
      const response = await siteApi.nextAvailableReference({
        projectId: projectUuid,
        siteId: siteUuid,
      });
      this.nextAvailableReference = response.nextAvailableReference;
      return this.nextAvailableReference;
    },
  },
});
